// Companion/ChatBlockThreadMarks.tsx
//
// The marks a QBank block thread leaves between messages (ORA-1285):
//
// - a thin "Question N" divider before the first message about a new question,
//   and "Question N" on each message's label, in this block's thread only;
//   from the block position each user message's context label carries (the
//   server persists it; a pending message has the client's own until then);
// - the same divider at the end of the thread once the student has moved to a
//   question they have not asked about yet;
// - the line a scope change leaves ("This chat now covers the whole block.").

import { createSelector } from '@reduxjs/toolkit';
import { Check, Question } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';
import {
  useChatBlockThreadState,
  type ChatBlockScope,
} from '@/chatContext/chatBlockThread';
import {
  selectConversationMessageIds,
  type ChatRootState,
} from '@/slices/chat/chatSelectors';
import type { ChatContextLabel as ChatContextLabelValue } from '@/types/chatStream';
import ChatContextLabel from './ChatContextLabel';

const NOTICE_TEXT: Record<ChatBlockScope, string> = {
  block: 'This chat now covers the whole block.',
  question: 'Each new question will start its own chat.',
};

interface QuestionMarks {
  /** User message id → the question its divider names. */
  dividers: Map<string, number>;
  /** The question of the latest user message that has one. */
  lastIndex: number | null;
}

/**
 * One pass over a conversation's user messages. A message without a position
 * (a detached question, another kind of context) neither draws a divider nor
 * resets the run, and the first positioned message draws none: the thread
 * starts on its question.
 */
export const selectQuestionMarks = createSelector(
  [
    (state: ChatRootState, conversationId: string) =>
      selectConversationMessageIds(state, conversationId),
    (state: ChatRootState) => state.chat.messages,
  ],
  (messageIds, messages): QuestionMarks => {
    const dividers = new Map<string, number>();
    let lastIndex: number | null = null;

    messageIds.forEach((messageId) => {
      const message = messages[messageId];
      if (message?.role !== 'user') return;

      const index = message.contextLabel?.blockPosition?.index;
      if (index === undefined) return;

      if (lastIndex !== null && index !== lastIndex) {
        dividers.set(messageId, index);
      }
      lastIndex = index;
    });

    return { dividers, lastIndex };
  }
);

/**
 * The conversation is the one chat of the block on screen. A thread kept for
 * another block (the same question can sit in two) is a plain question chat
 * here. Off a block's route, any block thread reads as one.
 */
export const isBlockThreadHere = (
  questionBlockId: string | null | undefined,
  currentBlockId: string | null
): boolean =>
  Boolean(questionBlockId) &&
  (currentBlockId === null || questionBlockId === currentBlockId);

const useIsBlockThreadHere = (conversationId: string): boolean => {
  const { current } = useChatBlockThreadState();

  const questionBlockId = useSelector(
    (state: ChatRootState) =>
      state.chat.conversations[conversationId]?.questionBlockId
  );

  return isBlockThreadHere(questionBlockId, current?.blockId ?? null);
};

const QuestionDivider = ({ index }: { index: number }) => (
  <div
    role="separator"
    aria-label={`Question ${index}`}
    data-testid="chat-block-divider"
    className="my-1 flex items-center gap-2.5 self-stretch font-heading text-xs font-medium text-fg-secondary before:h-px before:flex-1 before:bg-border-subtle after:h-px after:flex-1 after:bg-border-subtle"
  >
    <span className="inline-flex items-center gap-1">
      <Question size={12} aria-hidden="true" />
      Question {index}
    </span>
  </div>
);

/** Before a user message: the divider, when it starts a new question. */
export const ChatBlockDivider = ({
  conversationId,
  messageId,
}: {
  conversationId: string;
  messageId: string;
}) => {
  const inBlockThread = useIsBlockThreadHere(conversationId);

  const index = useSelector(
    (state: ChatRootState) =>
      selectQuestionMarks(state, conversationId).dividers.get(messageId) ?? null
  );

  return inBlockThread && index !== null ? (
    <QuestionDivider index={index} />
  ) : null;
};

/** A user message's context label, which reads "Question N" in a block thread. */
export const ChatBlockContextLabel = ({
  conversationId,
  context,
}: {
  conversationId: string;
  context: ChatContextLabelValue;
}) => (
  <ChatContextLabel
    context={context}
    inBlockThread={useIsBlockThreadHere(conversationId)}
  />
);

/** After a message: the scope line it left, and the thread's trailing divider. */
export const ChatBlockThreadTrail = ({
  conversationId,
  messageId,
}: {
  conversationId: string;
  messageId: string;
}) => {
  const { current, scopes, notices } = useChatBlockThreadState();
  const notice = notices[conversationId];
  const inBlockThread = useIsBlockThreadHere(conversationId);

  const isActiveTail = useSelector((state: ChatRootState) => {
    if (state.chat.activeConversationId !== conversationId) return false;
    const messageIds = selectConversationMessageIds(state, conversationId);
    return messageIds[messageIds.length - 1] === messageId;
  });

  const lastIndex = useSelector(
    (state: ChatRootState) =>
      selectQuestionMarks(state, conversationId).lastIndex
  );

  const nextIndex =
    isActiveTail &&
    inBlockThread &&
    current &&
    scopes[current.blockId] === 'block' &&
    lastIndex !== null &&
    lastIndex !== current.index
      ? current.index
      : null;

  const showsNotice = notice?.afterMessageId === messageId;
  if (!showsNotice && nextIndex === null) return null;

  return (
    <>
      {showsNotice ? (
        <p
          data-testid="chat-block-scope-notice"
          className="m-0 my-1 flex items-center justify-center gap-1.5 self-center text-xs text-fg-secondary"
        >
          <Check
            size={14}
            aria-hidden="true"
            className="text-green-600 dark:text-green-400"
          />
          {NOTICE_TEXT[notice.scope]}
        </p>
      ) : null}
      {nextIndex !== null ? <QuestionDivider index={nextIndex} /> : null}
    </>
  );
};
